import { useEffect, useRef, useState } from 'react';

import { api } from '../../services/api';
import type { AdRecord, AdPlatform, AdPlatformSpec, SocialAccount, AdAutomation, SocialSettings } from '../../types';

type SubTab = 'compose' | 'posts' | 'calendar' | 'queue' | 'accounts' | 'analytics' | 'automations' | 'settings';

const PLATFORM_ICONS: Record<string, string> = {
  whatsapp: '📱', facebook: 'f', instagram: '📷', x: '𝕏', tiktok: '♪', youtube: '▶', linkedin: 'in', ads: '📢',
};

const PLATFORM_COLORS: Record<string, string> = {
  whatsapp: '#25D366', facebook: '#1877F2', instagram: '#E4405F', x: '#000', tiktok: '#010101', youtube: '#FF0000', linkedin: '#0A66C2', ads: '#F4B400',
};

const STATUS_LABELS: Record<string, string> = {
  draft: 'Concept', scheduled: 'Gepland', publishing: 'Publiceren…', published: 'Gepubliceerd', failed: 'Mislukt', cancelled: 'Geannuleerd',
};

const TONES = ['professional', 'casual', 'promotional', 'urgent', 'friendly', 'bold'];

const AdsManager = () => {
  const [subTab, setSubTab] = useState<SubTab>('compose');
  const [ads, setAds] = useState<AdRecord[]>([]);
  const [accounts, setAccounts] = useState<SocialAccount[]>([]);
  const [automations, setAutomations] = useState<AdAutomation[]>([]);
  const [socialSettings, setSocialSettings] = useState<SocialSettings | null>(null);
  const [platforms, setPlatforms] = useState<Record<string, AdPlatformSpec>>({});
  const [loading, setLoading] = useState(true);
  const loadedRef = useRef(false);

  useEffect(() => {
    if (loadedRef.current) return;
    loadedRef.current = true;
    void Promise.all([
      api.ads().catch(() => [] as AdRecord[]),
      api.socialAccounts().catch(() => [] as SocialAccount[]),
      api.adAutomations().catch(() => [] as AdAutomation[]),
      api.socialSettings().catch(() => null),
      api.adPlatforms().catch(() => ({})),
    ]).then(([a, acc, auto, settings, plats]) => {
      setAds(a as AdRecord[]);
      setAccounts(acc as SocialAccount[]);
      setAutomations(auto as AdAutomation[]);
      setSocialSettings(settings as SocialSettings | null);
      setPlatforms(plats as Record<string, AdPlatformSpec>);
      setLoading(false);
    });
  }, []);

  const reloadAds = () => api.ads().then(setAds).catch(() => undefined);
  const reloadAccounts = () => api.socialAccounts().then(setAccounts).catch(() => undefined);
  const reloadAutomations = () => api.adAutomations().then(setAutomations).catch(() => undefined);

  const subTabs: { key: SubTab; icon: string; label: string }[] = [
    { key: 'compose', icon: '✍️', label: 'Compose' },
    { key: 'posts', icon: '📝', label: 'Posts' },
    { key: 'calendar', icon: '📅', label: 'Calendar' },
    { key: 'queue', icon: '⏰', label: 'Queue' },
    { key: 'accounts', icon: '🔗', label: 'Accounts' },
    { key: 'analytics', icon: '📈', label: 'Analytics' },
    { key: 'automations', icon: '⚡', label: 'Automations' },
    { key: 'settings', icon: '⚙️', label: 'Settings' },
  ];

  return (
    <div className="ads-manager">
      <header className="panel-header">
        <div>
          <p className="eyebrow">Social Marketing</p>
          <h2>Ads & Social Posts</h2>
        </div>
      </header>

      <nav className="ads-subtabs">
        {subTabs.map((tab) => (
          <button
            key={tab.key}
            type="button"
            className={`ads-subtab ${subTab === tab.key ? 'active' : ''}`}
            onClick={() => setSubTab(tab.key)}
          >
            <span aria-hidden="true">{tab.icon}</span> {tab.label}
          </button>
        ))}
      </nav>

      {loading && <p className="muted small">Laden…</p>}

      {!loading && subTab === 'compose' && (
        <ComposeView platforms={platforms} accounts={accounts} onCreated={reloadAds} />
      )}
      {!loading && subTab === 'posts' && (
        <PostsView ads={ads} onReload={reloadAds} />
      )}
      {!loading && subTab === 'calendar' && (
        <CalendarView ads={ads} />
      )}
      {!loading && subTab === 'queue' && (
        <QueueView ads={ads} onReload={reloadAds} />
      )}
      {!loading && subTab === 'accounts' && (
        <AccountsView accounts={accounts} onReload={reloadAccounts} />
      )}
      {!loading && subTab === 'analytics' && (
        <AnalyticsView ads={ads} />
      )}
      {!loading && subTab === 'automations' && (
        <AutomationsView automations={automations} accounts={accounts} platforms={platforms} onReload={reloadAutomations} />
      )}
      {!loading && subTab === 'settings' && (
        <SettingsView settings={socialSettings} onReload={() => api.socialSettings().then(setSocialSettings).catch(() => undefined)} />
      )}
    </div>
  );
};

// ── Compose View ──
type ComposeProps = { platforms: Record<string, AdPlatformSpec>; accounts: SocialAccount[]; onCreated: () => void };

const ComposeView = ({ platforms, accounts, onCreated }: ComposeProps) => {
  const [content, setContent] = useState('');
  const [title, setTitle] = useState('');
  const [selectedPlatforms, setSelectedPlatforms] = useState<Set<AdPlatform>>(new Set());
  const [hashtags, setHashtags] = useState('');
  const [linkUrl, setLinkUrl] = useState('');
  const [whatsappPhone, setWhatsappPhone] = useState('');
  const [selectedTone, setSelectedTone] = useState('professional');
  const [aiGenerating, setAiGenerating] = useState(false);
  const [scheduleAt, setScheduleAt] = useState('');
  const [showSchedule, setShowSchedule] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [mediaPreview, setMediaPreview] = useState<string[]>([]);
  const [, setSelectedTemplate] = useState<{ platform: string; template: { type: string; emoji: string; title: string; content: string } } | null>(null);

  const togglePlatform = (p: AdPlatform) => {
    setSelectedPlatforms((prev) => {
      const next = new Set(prev);
      if (next.has(p)) next.delete(p);
      else next.add(p);
      return next;
    });
  };

  const handleAIGenerate = async () => {
    setAiGenerating(true);
    setError(null);
    try {
      const platformList = [...selectedPlatforms].join(', ') || 'social media';
      const prompt = `Write engaging ad copy for ${platformList}. ${title ? `About: ${title}.` : ''} Tone: ${selectedTone}. Include a hook, value proposition, and call-to-action.`;
      const result = await api.aiTask({
        task: 'ad_copy',
        messages: [{ role: 'user', content: prompt }],
      });
      setContent(result.result.content);
    } catch {
      setError('AI generatie mislukt. Controleer of de AI provider is geconfigureerd.');
    } finally {
      setAiGenerating(false);
    }
  };

  const handleApplyTemplate = (platform: string, template: { type: string; emoji: string; title: string; content: string }) => {
    setSelectedTemplate({ platform, template });
    setContent(template.content);
    if (!title) setTitle(template.title);
  };

  const handleSave = async (publish: boolean) => {
    if (!content.trim()) return;
    setSaving(true);
    setError(null);
    setSuccess(null);
    try {
      const adInput: Partial<AdRecord> = {
        title: title.trim() || 'Untitled Ad',
        content: content.trim(),
        contentType: mediaPreview.length > 0 ? 'image' : 'text',
        mediaUrls: mediaPreview,
        platforms: [...selectedPlatforms],
        hashtags: hashtags.trim(),
        linkUrl: linkUrl.trim(),
        status: publish ? 'publishing' : 'draft',
        aiGenerated: false,
        clicks: 0,
        conversions: 0,
      };
      if (whatsappPhone.trim()) adInput.whatsappPhone = whatsappPhone.trim();
      const ad = await api.createAd(adInput);
      if (publish) {
        await api.publishAd(ad.id);
        setSuccess('Ad gepubliceerd!');
      } else if (scheduleAt) {
        await api.scheduleAd(ad.id, new Date(scheduleAt).toISOString());
        setSuccess('Ad ingepland!');
      } else {
        setSuccess('Ad opgeslagen als concept.');
      }
      setContent('');
      setTitle('');
      setHashtags('');
      setLinkUrl('');
      setWhatsappPhone('');
      setMediaPreview([]);
      setSelectedPlatforms(new Set());
      setScheduleAt('');
      setShowSchedule(false);
      onCreated();
    } catch {
      setError('Opslaan mislukt.');
    } finally {
      setSaving(false);
    }
  };

  const maxChars = Math.min(...[...selectedPlatforms].map((p) => platforms[p]?.maxChars ?? 4096), 4096);
  const charCount = content.length;
  const connectedPlatforms = accounts.filter((a) => a.status === 'active').map((a) => a.platform);

  return (
    <div className="ads-compose">
      <div className="ads-compose-main">
        {/* Platform selection */}
        <div className="compose-platforms">
          <label className="compose-label">Platforms</label>
          <div className="compose-platform-list">
            {Object.entries(platforms).filter(([key]) => key !== 'ads').map(([key, spec]) => {
              const isConnected = connectedPlatforms.includes(key as AdPlatform);
              return (
                <button
                  key={key}
                  type="button"
                  className={`compose-platform-btn ${selectedPlatforms.has(key as AdPlatform) ? 'selected' : ''} ${!isConnected ? 'disabled' : ''}`}
                  onClick={() => togglePlatform(key as AdPlatform)}
                  disabled={!isConnected}
                  style={{ borderColor: selectedPlatforms.has(key as AdPlatform) ? PLATFORM_COLORS[key] : undefined }}
                >
                  <span className="platform-icon" style={{ background: PLATFORM_COLORS[key] }}>{PLATFORM_ICONS[key] ?? key.slice(0, 1)}</span>
                  <span>{spec.name}</span>
                  {!isConnected && <span className="platform-unavailable">Niet verbonden</span>}
                </button>
              );
            })}
          </div>
        </div>

        {/* Title */}
        <label className="compose-field">
          <span>Titel (intern)</span>
          <input type="text" value={title} onChange={(e) => setTitle(e.target.value)} placeholder="Bijv. Zomer sale promo" maxLength={200} />
        </label>

        {/* Content */}
        <label className="compose-field">
          <span>Ad tekst</span>
          <textarea
            value={content}
            onChange={(e) => setContent(e.target.value)}
            placeholder="Schrijf je ad tekst…"
            rows={6}
            maxLength={4096}
          />
          <div className={`char-count ${charCount > maxChars ? 'over' : charCount > maxChars * 0.8 ? 'warn' : ''}`}>
            {charCount} / {maxChars}
          </div>
        </label>

        {/* AI panel */}
        <div className="compose-ai-panel">
          <div className="compose-ai-header">
            <span>🤖 AI Content Generatie</span>
          </div>
          <div className="compose-tone-btns">
            {TONES.map((tone) => (
              <button
                key={tone}
                type="button"
                className={`compose-tone-btn ${selectedTone === tone ? 'active' : ''}`}
                onClick={() => setSelectedTone(tone)}
              >
                {tone}
              </button>
            ))}
          </div>
          <button
            type="button"
            className="button primary small"
            disabled={aiGenerating}
            onClick={() => void handleAIGenerate()}
          >
            {aiGenerating ? 'Genereren…' : '✨ Genereer ad tekst'}
          </button>
        </div>

        {/* Media */}
        <div className="compose-media-zone">
          <p className="muted small">📸 Media upload (binnenkort — sleep foto's of video's hier)</p>
          {mediaPreview.length > 0 && (
            <div className="compose-media-thumbs">
              {mediaPreview.map((url, i) => (
                <div key={i} className="compose-media-thumb">
                  <img src={url} alt="" />
                  <button type="button" className="media-remove" onClick={() => setMediaPreview((prev) => prev.filter((_, idx) => idx !== i))}>×</button>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Hashtags & Link */}
        <div className="compose-row">
          <label className="compose-field">
            <span>Hashtags</span>
            <input type="text" value={hashtags} onChange={(e) => setHashtags(e.target.value)} placeholder="#sale #promo" maxLength={500} />
          </label>
          <label className="compose-field">
            <span>Link URL</span>
            <input type="text" value={linkUrl} onChange={(e) => setLinkUrl(e.target.value)} placeholder="https://…" maxLength={500} />
          </label>
        </div>

        {/* WhatsApp phone for click-to-WhatsApp */}
        <label className="compose-field">
          <span>WhatsApp telefoonnummer (voor Click-to-WhatsApp)</span>
          <input type="tel" value={whatsappPhone} onChange={(e) => setWhatsappPhone(e.target.value)} placeholder="+31612345678" />
        </label>

        {/* Schedule */}
        {showSchedule && (
          <label className="compose-field">
            <span>Publiceren op</span>
            <input
              type="datetime-local"
              value={scheduleAt}
              onChange={(e) => setScheduleAt(e.target.value)}
            />
          </label>
        )}

        {/* Actions */}
        <div className="compose-actions">
          <button type="button" className="button secondary small" disabled={saving || !content.trim()} onClick={() => void handleSave(false)}>
            {saving ? 'Opslaan…' : '💾 Concept opslaan'}
          </button>
          <button type="button" className="button secondary small" disabled={saving || !content.trim()} onClick={() => setShowSchedule((p) => !p)}>
            📅 {showSchedule ? 'Verberg schema' : 'Inplannen'}
          </button>
          {showSchedule && scheduleAt && (
            <button type="button" className="button primary small" disabled={saving || !content.trim() || !scheduleAt} onClick={() => void handleSave(false)}>
              ✅ Inplannen
            </button>
          )}
          <button type="button" className="button primary small" disabled={saving || !content.trim()} onClick={() => void handleSave(true)}>
            🚀 Nu publiceren
          </button>
        </div>

        {error && <p className="notice error small">{error}</p>}
        {success && <p className="notice success small">{success}</p>}
      </div>

      {/* Template Library sidebar */}
      <div className="ads-compose-sidebar">
        <h3>📚 Templates</h3>
        {Object.entries(platforms).filter(([key]) => key !== 'ads' && (selectedPlatforms.size === 0 || selectedPlatforms.has(key as AdPlatform))).map(([key, spec]) => (
          <div key={key} className="template-platform-section">
            <div className="template-platform-header">
              <span className="platform-icon small" style={{ background: PLATFORM_COLORS[key] }}>{PLATFORM_ICONS[key] ?? key.slice(0, 1)}</span>
              <span>{spec.name}</span>
              <span className="muted small">{spec.templates.length} templates</span>
            </div>
            <div className="template-list">
              {spec.templates.map((tpl, i) => (
                <button
                  key={i}
                  type="button"
                  className="template-item-btn"
                  onClick={() => handleApplyTemplate(key, tpl)}
                >
                  <span className="template-emoji">{tpl.emoji}</span>
                  <span className="template-title">{tpl.title}</span>
                </button>
              ))}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

// ── Posts View ──
const PostsView = ({ ads, onReload }: { ads: AdRecord[]; onReload: () => void }) => {
  const handleDelete = async (id: string) => {
    if (!confirm('Weet je zeker dat je deze ad wilt verwijderen?')) return;
    try { await api.deleteAd(id); onReload(); } catch { /* ignore */ }
  };
  return (
    <div className="ads-posts">
      {ads.length === 0 && <p className="empty-state small">Nog geen ads. Ga naar Compose om er een te maken.</p>}
      {ads.map((ad) => (
        <div key={ad.id} className={`ad-post-card ${ad.status}`}>
          <div className="ad-post-header">
            <strong>{ad.title}</strong>
            <span className={`ad-status ${ad.status}`}>{STATUS_LABELS[ad.status] ?? ad.status}</span>
          </div>
          <p className="ad-post-content">{ad.content.slice(0, 200)}{ad.content.length > 200 ? '…' : ''}</p>
          <div className="ad-post-meta">
            <div className="ad-post-platforms">
              {ad.platforms.map((p) => (
                <span key={p} className="platform-icon tiny" style={{ background: PLATFORM_COLORS[p] }}>{PLATFORM_ICONS[p] ?? p.slice(0, 1)}</span>
              ))}
            </div>
            <span className="muted small">📅 {new Date(ad.createdAt).toLocaleDateString('nl-NL')}</span>
            <span className="muted small">👆 {ad.clicks} clicks</span>
          </div>
          <div className="ad-post-actions">
            <button type="button" className="button secondary small" onClick={() => void navigator.clipboard.writeText(ad.linkUrl || `https://ai-messagebot.app/ads/?ad=${ad.id}`)}>🔗 Link kopiëren</button>
            {ad.status === 'draft' && (
              <button type="button" className="button primary small" onClick={() => { void api.publishAd(ad.id).then(onReload); }}>🚀 Publiceren</button>
            )}
            <button type="button" className="button danger small" onClick={() => void handleDelete(ad.id)}>×</button>
          </div>
        </div>
      ))}
    </div>
  );
};

// ── Calendar View ──
const CalendarView = ({ ads }: { ads: AdRecord[] }) => {
  const scheduled = ads.filter((a) => a.status === 'scheduled' && a.scheduledAt);
  const published = ads.filter((a) => a.status === 'published' && a.publishedAt);
  return (
    <div className="ads-calendar">
      <h3>📅 Geplande posts</h3>
      {scheduled.length === 0 && <p className="empty-state small">Geen geplande posts.</p>}
      {scheduled.map((ad) => (
        <div key={ad.id} className="calendar-item">
          <span className="calendar-date">{new Date(ad.scheduledAt!).toLocaleString('nl-NL')}</span>
          <strong>{ad.title}</strong>
        </div>
      ))}
      <h3 style={{ marginTop: '1.5rem' }}>✅ Gepubliceerde posts</h3>
      {published.length === 0 && <p className="empty-state small">Nog geen gepubliceerde posts.</p>}
      {published.map((ad) => (
        <div key={ad.id} className="calendar-item published">
          <span className="calendar-date">{new Date(ad.publishedAt!).toLocaleString('nl-NL')}</span>
          <strong>{ad.title}</strong>
          <span className="muted small">👆 {ad.clicks} clicks</span>
        </div>
      ))}
    </div>
  );
};

// ── Queue View ──
const QueueView = ({ ads, onReload }: { ads: AdRecord[]; onReload: () => void }) => {
  const queue = ads.filter((a) => a.status === 'scheduled' || a.status === 'publishing');
  const handleCancel = async (id: string) => {
    try { await api.updateAd(id, { status: 'cancelled' }); onReload(); } catch { /* ignore */ }
  };
  return (
    <div className="ads-queue">
      <h3>⏰ Publishing Queue</h3>
      {queue.length === 0 && <p className="empty-state small">Geen posts in de queue.</p>}
      {queue.map((ad) => (
        <div key={ad.id} className="queue-item">
          <div>
            <strong>{ad.title}</strong>
            <span className={`ad-status ${ad.status}`}>{STATUS_LABELS[ad.status]}</span>
          </div>
          {ad.scheduledAt && <span className="muted small">{new Date(ad.scheduledAt).toLocaleString('nl-NL')}</span>}
          <button type="button" className="button danger small" onClick={() => void handleCancel(ad.id)}>Annuleren</button>
        </div>
      ))}
    </div>
  );
};

// ── Accounts View ──
const AccountsView = ({ accounts, onReload }: { accounts: SocialAccount[]; onReload: () => void }) => {
  const handleDisconnect = async (id: string) => {
    try { await api.disconnectSocialAccount(id); onReload(); } catch { /* ignore */ }
  };

  const handleTest = async (id: string) => {
    try { await api.testSocialAccount(id); } catch { /* ignore */ }
  };

  return (
    <div className="ads-accounts">
      <h3>🔗 Verbonden Accounts</h3>
      <p className="muted small" style={{ marginBottom: '0.8rem' }}>
        Beheer je API credentials in <strong>Instellingen → Social Marketing API</strong>.
      </p>
      {accounts.length === 0 && (
        <div className="empty-state small">
          <p>Nog geen accounts verbonden.</p>
          <p className="muted">Ga naar Instellingen om je social media accounts te koppelen.</p>
        </div>
      )}
      {accounts.map((acc) => (
        <div key={acc.id} className={`account-card ${acc.status}`}>
          <span className="platform-icon" style={{ background: PLATFORM_COLORS[acc.platform] }}>{PLATFORM_ICONS[acc.platform] ?? acc.platform.slice(0, 1)}</span>
          <div className="account-info">
            <strong>{acc.accountName}</strong>
            <span className={`muted small status-${acc.status}`}>{acc.status === 'active' ? '✅ Verbonden' : '⚠ ' + acc.status}</span>
          </div>
          <button type="button" className="button secondary small" onClick={() => void handleTest(acc.id)}>Test</button>
          <button type="button" className="button danger small" onClick={() => void handleDisconnect(acc.id)}>Verbreek</button>
        </div>
      ))}
    </div>
  );
};

// ── Analytics View ──
const AnalyticsView = ({ ads }: { ads: AdRecord[] }) => {
  const published = ads.filter((a) => a.status === 'published');
  const totalClicks = published.reduce((sum, ad) => sum + ad.clicks, 0);
  const totalConversions = published.reduce((sum, ad) => sum + ad.conversions, 0);
  const ctr = totalClicks > 0 ? ((totalConversions / totalClicks) * 100).toFixed(1) : '0';
  return (
    <div className="ads-analytics">
      <h3>📈 Analytics Overview</h3>
      <div className="analytics-stats">
        <div className="analytics-stat"><span className="stat-value">{published.length}</span><span className="stat-label">Gepubliceerd</span></div>
        <div className="analytics-stat"><span className="stat-value">{totalClicks}</span><span className="stat-label">Clicks</span></div>
        <div className="analytics-stat"><span className="stat-value">{totalConversions}</span><span className="stat-label">Conversies</span></div>
        <div className="analytics-stat"><span className="stat-value">{ctr}%</span><span className="stat-label">CTR</span></div>
      </div>
      <h3>Top Ads</h3>
      {published.length === 0 && <p className="empty-state small">Nog geen gepubliceerde ads.</p>}
      {[...published].sort((a, b) => b.clicks - a.clicks).slice(0, 10).map((ad) => (
        <div key={ad.id} className="analytics-ad-row">
          <strong>{ad.title}</strong>
          <span className="muted small">👆 {ad.clicks} clicks · ✅ {ad.conversions} conversies</span>
        </div>
      ))}
    </div>
  );
};

// ── Automations View ──
const AutomationsView = ({ automations, accounts, platforms, onReload }: { automations: AdAutomation[]; accounts: SocialAccount[]; platforms: Record<string, AdPlatformSpec>; onReload: () => void }) => {
  const [showForm, setShowForm] = useState(false);
  const [name, setName] = useState('');
  const [type, setType] = useState<'ai_auto' | 'rss_auto' | 'schedule_repeat'>('ai_auto');
  const [frequency, setFrequency] = useState('daily');
  const [timeSlot, setTimeSlot] = useState('09:00');
  const [prompt, setPrompt] = useState('');
  const [tone, setTone] = useState('professional');
  const [selectedPlatforms, setSelectedPlatforms] = useState<Set<AdPlatform>>(new Set());
  const [saving, setSaving] = useState(false);

  const handleCreate = async () => {
    if (!name.trim()) return;
    setSaving(true);
    try {
      await api.createAdAutomation({
        name: name.trim(),
        type,
        platforms: [...selectedPlatforms],
        accountIds: accounts.filter((a) => selectedPlatforms.has(a.platform)).map((a) => a.id),
        frequency,
        timeSlot,
        prompt: prompt.trim(),
        tone,
        isActive: true,
        runCount: 0,
      });
      setName(''); setPrompt(''); setSelectedPlatforms(new Set());
      setShowForm(false);
      onReload();
    } catch { /* ignore */ }
    setSaving(false);
  };

  const handleToggle = async (auto: AdAutomation) => {
    try { await api.updateAdAutomation(auto.id, { isActive: !auto.isActive }); onReload(); } catch { /* ignore */ }
  };

  const handleDelete = async (id: string) => {
    try { await api.deleteAdAutomation(id); onReload(); } catch { /* ignore */ }
  };

  return (
    <div className="ads-automations">
      <div className="automations-header">
        <h3>⚡ Automations</h3>
        <button type="button" className="button primary small" onClick={() => setShowForm((p) => !p)}>+ Nieuwe automatisering</button>
      </div>
      {showForm && (
        <div className="automation-form">
          <label className="compose-field">
            <span>Naam</span>
            <input type="text" value={name} onChange={(e) => setName(e.target.value)} placeholder="Bijv. Dagelijkse AI posts" />
          </label>
          <label className="compose-field">
            <span>Type</span>
            <select value={type} onChange={(e) => setType(e.target.value as typeof type)}>
              <option value="ai_auto">AI Auto-posting</option>
              <option value="rss_auto">RSS Auto-posting</option>
              <option value="schedule_repeat">Herhalend schema</option>
            </select>
          </label>
          <div className="compose-row">
            <label className="compose-field">
              <span>Frequentie</span>
              <select value={frequency} onChange={(e) => setFrequency(e.target.value)}>
                <option value="hourly">Elk uur</option>
                <option value="daily">Dagelijks</option>
                <option value="weekly">Wekelijks</option>
                <option value="2x_day">2× per dag</option>
                <option value="3x_week">3× per week</option>
              </select>
            </label>
            <label className="compose-field">
              <span>Tijdstip</span>
              <input type="time" value={timeSlot} onChange={(e) => setTimeSlot(e.target.value)} />
            </label>
          </div>
          <label className="compose-field">
            <span>Platforms</span>
            <div className="compose-platform-list">
              {Object.entries(platforms).filter(([key]) => key !== 'ads').map(([key, spec]) => (
                <button
                  key={key}
                  type="button"
                  className={`compose-platform-btn small ${selectedPlatforms.has(key as AdPlatform) ? 'selected' : ''}`}
                  onClick={() => {
                    const next = new Set(selectedPlatforms);
                    if (next.has(key as AdPlatform)) next.delete(key as AdPlatform);
                    else next.add(key as AdPlatform);
                    setSelectedPlatforms(next);
                  }}
                >
                  <span className="platform-icon tiny" style={{ background: PLATFORM_COLORS[key] }}>{PLATFORM_ICONS[key] ?? key.slice(0, 1)}</span>
                  {spec.name}
                </button>
              ))}
            </div>
          </label>
          <label className="compose-field">
            <span>AI prompt</span>
            <textarea value={prompt} onChange={(e) => setPrompt(e.target.value)} placeholder="Beschrijf wat de AI moet posten…" rows={3} />
          </label>
          <label className="compose-field">
            <span>Tone</span>
            <select value={tone} onChange={(e) => setTone(e.target.value)}>
              {TONES.map((t) => <option key={t} value={t}>{t}</option>)}
            </select>
          </label>
          <button type="button" className="button primary small" disabled={saving || !name.trim()} onClick={() => void handleCreate()}>
            {saving ? 'Opslaan…' : 'Automatisering aanmaken'}
          </button>
        </div>
      )}
      {automations.length === 0 && <p className="empty-state small">Nog geen automatiseringen.</p>}
      {automations.map((auto) => (
        <div key={auto.id} className="automation-row">
          <div className="automation-icon">⚡</div>
          <div className="automation-info">
            <strong>{auto.name}</strong>
            <span className="muted small">{auto.type} · {auto.frequency} · {auto.timeSlot} · {auto.runCount} runs</span>
          </div>
          <span className={`auto-badge ${auto.isActive ? 'active' : 'paused'}`}>{auto.isActive ? 'Actief' : 'Gepauzeerd'}</span>
          <button type="button" className="button secondary small" onClick={() => void handleToggle(auto)}>{auto.isActive ? '⏸' : '▶'}</button>
          <button type="button" className="button danger small" onClick={() => void handleDelete(auto.id)}>×</button>
        </div>
      ))}
    </div>
  );
};

// ── Settings View ──
const SettingsView = ({ settings, onReload }: { settings: SocialSettings | null; onReload: () => void }) => {
  const [tone, setTone] = useState(settings?.defaultTone ?? 'professional');
  const [language, setLanguage] = useState(settings?.defaultLanguage ?? 'nl');
  const [autoHashtags, setAutoHashtags] = useState(settings?.autoHashtags ?? true);
  const [shortLinks, setShortLinks] = useState(settings?.shortLinks ?? true);
  const [saving, setSaving] = useState(false);

  const handleSave = async () => {
    setSaving(true);
    try {
      await api.updateSocialSettings({ defaultTone: tone, defaultLanguage: language, autoHashtags, shortLinks });
      onReload();
    } catch { /* ignore */ }
    setSaving(false);
  };

  return (
    <div className="ads-settings">
      <h3>⚙️ Social Marketing Instellingen</h3>
      <label className="compose-field">
        <span>Standaard tone</span>
        <select value={tone} onChange={(e) => setTone(e.target.value)}>
          {TONES.map((t) => <option key={t} value={t}>{t}</option>)}
        </select>
      </label>
      <label className="compose-field">
        <span>Standaard taal</span>
        <select value={language} onChange={(e) => setLanguage(e.target.value)}>
          <option value="nl">Nederlands</option>
          <option value="en">English</option>
          <option value="es">Español</option>
          <option value="de">Deutsch</option>
          <option value="fr">Français</option>
        </select>
      </label>
      <label className="compose-toggle-row">
        <div>
          <strong>Automatische hashtags</strong>
          <p className="muted small">Genereer automatisch relevante hashtags bij AI content.</p>
        </div>
        <input type="checkbox" checked={autoHashtags} onChange={(e) => setAutoHashtags(e.target.checked)} />
      </label>
      <label className="compose-toggle-row">
        <div>
          <strong>Short links</strong>
          <p className="muted small">Gebruik verkorte links voor click tracking.</p>
        </div>
        <input type="checkbox" checked={shortLinks} onChange={(e) => setShortLinks(e.target.checked)} />
      </label>
      <button type="button" className="button primary small" disabled={saving} onClick={() => void handleSave()}>
        {saving ? 'Opslaan…' : 'Opslaan'}
      </button>
    </div>
  );
};

export default AdsManager;
